/**
 * Reviews Service
 * Handles all review-related API calls with mock fallback
 */

import { apiService, ApiResponse } from './api.service';

/**
 * Review Interface
 */
export interface Review {
  id: string;
  productId: string;
  productName?: string;
  customerId: string;
  customerName: string;
  rating: number;
  title?: string;
  comment?: string;
  orderId?: string;
  isVerified: boolean;          // ← matches backend field name
  helpfulCount?: number;
  status?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Create Review DTO
 */
export interface CreateReviewDto {
  productId: string;
  customerId: string;
  rating: number;
  comment?: string;
  orderId?: string;
}

/**
 * Update Review DTO
 */
export interface UpdateReviewDto {
  rating?: number;
  comment?: string;
}

/**
 * Reviews Query Parameters
 */
export interface ReviewsQuery {
  page?: number;
  limit?: number;
  productId?: string;
  customerId?: string;
  rating?: number;
  minRating?: number;
  maxRating?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * Paginated Reviews Response
 */
export interface PaginatedReviews {
  data: Review[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  averageRating: number;
  ratingDistribution: {
    1: number;
    2: number;
    3: number;
    4: number;
    5: number;
  };
}

/**
 * Review Statistics
 */
export interface ReviewStats {
  totalReviews: number;
  averageRating: number;
  verifiedReviews: number;      // ← matches backend field name
  pendingReviews: number;
  approvedReviews: number;
  rejectedReviews: number;
  flaggedReviews: number;
  ratingDistribution: {
    1: number;
    2: number;
    3: number;
    4: number;
    5: number;
  };
}

// ---------------------------------------------------------------------------
// Mock Data — used as fallback when the backend /reviews endpoint is absent
// ---------------------------------------------------------------------------
const MOCK_REVIEWS: Review[] = [
  {
    id: 'rev-001',
    productId: 'prod-01',
    productName: 'Grilled Salmon',
    customerId: 'cust-01',
    customerName: 'Sophia Martinez',
    rating: 5,
    comment: 'Absolutely outstanding! The salmon was perfectly cooked, moist and flavorful. The seasoning was spot-on and the presentation was beautiful. Will definitely order again!',
    isVerified: true,
    createdAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'rev-002',
    productId: 'prod-02',
    productName: 'Truffle Pasta',
    customerId: 'cust-02',
    customerName: 'James Nguyen',
    rating: 4,
    comment: 'Really enjoyed the truffle pasta. Rich, creamy sauce and the pasta was cooked al dente just how I like it. Only minor thing was it could use a touch more salt.',
    isVerified: true,
    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'rev-003',
    productId: 'prod-03',
    productName: 'Wagyu Beef Burger',
    customerId: 'cust-03',
    customerName: 'Aisha Okafor',
    rating: 5,
    comment: 'Best burger I have ever had! The wagyu beef was incredibly tender and juicy. The brioche bun was toasted to perfection. This is now my go-to order.',
    isVerified: true,
    createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'rev-004',
    productId: 'prod-04',
    productName: 'Caesar Salad',
    customerId: 'cust-04',
    customerName: "Liam O'Brien",
    rating: 3,
    comment: 'The salad was fresh and the croutons were crunchy. However the Caesar dressing felt a bit too heavy for my taste. Average experience overall.',
    isVerified: false,
    createdAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'rev-005',
    productId: 'prod-05',
    productName: 'Chocolate Lava Cake',
    customerId: 'cust-05',
    customerName: 'Priya Sharma',
    rating: 5,
    comment: 'The lava cake was divine! Warm, gooey chocolate center with a perfectly crispy exterior. Paired beautifully with the vanilla ice cream on the side. A must-try!',
    isVerified: true,
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'rev-006',
    productId: 'prod-06',
    productName: 'Lobster Bisque',
    customerId: 'cust-06',
    customerName: 'Carlos Reyes',
    rating: 4,
    comment: 'Very creamy and flavorful bisque with generous chunks of lobster. Presentation was elegant. Slightly pricey but worth it for a special occasion.',
    isVerified: true,
    createdAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'rev-007',
    productId: 'prod-07',
    productName: 'Margherita Pizza',
    customerId: 'cust-07',
    customerName: 'Emma Johansson',
    rating: 4,
    comment: 'Classic done right. Fresh mozzarella, sweet tomatoes and fragrant basil on a beautifully thin, crispy crust. Simple yet satisfying.',
    isVerified: true,
    createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'rev-008',
    productId: 'prod-08',
    productName: 'Chicken Tikka Masala',
    customerId: 'cust-08',
    customerName: 'David Park',
    rating: 2,
    comment: "Expected more depth of flavour. The sauce was thin and the chicken was slightly overcooked. The naan bread was good though. Probably won't order again.",
    isVerified: false,
    createdAt: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'rev-009',
    productId: 'prod-09',
    productName: 'Avocado Toast',
    customerId: 'cust-09',
    customerName: 'Fatima Al-Hassan',
    rating: 5,
    comment: 'Perfectly ripe avocado on sourdough with a lovely poached egg. The chilli flakes and microgreens added great texture. Fresh, healthy and delicious.',
    isVerified: true,
    createdAt: new Date(Date.now() - 11 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 11 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'rev-010',
    productId: 'prod-10',
    productName: 'Beef Tacos',
    customerId: 'cust-10',
    customerName: 'Noah Williams',
    rating: 4,
    comment: 'Tasty tacos with well-seasoned beef and fresh toppings. The salsa verde was a highlight. A little messy to eat but absolutely worth it!',
    isVerified: true,
    createdAt: new Date(Date.now() - 13 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 13 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'rev-011',
    productId: 'prod-01',
    productName: 'Grilled Salmon',
    customerId: 'cust-11',
    customerName: 'Olivia Chen',
    rating: 5,
    comment: 'Second time ordering and it just keeps getting better. Impeccable quality and consistency. The lemon butter sauce is to die for.',
    isVerified: true,
    createdAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'rev-012',
    productId: 'prod-11',
    productName: 'Mushroom Risotto',
    customerId: 'cust-12',
    customerName: 'Hassan Ibrahim',
    rating: 3,
    comment: 'Decent risotto but nothing exceptional. The mushroom flavour could be more pronounced and I found it a bit too starchy. Presentation was nice though.',
    isVerified: true,
    createdAt: new Date(Date.now() - 18 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 18 * 24 * 60 * 60 * 1000).toISOString(),
  },
];

function buildPaginatedResponse(reviews: Review[], query?: ReviewsQuery): PaginatedReviews {
  const page = query?.page ?? 1;
  const limit = query?.limit ?? 10;
  const total = reviews.length;
  const totalPages = Math.ceil(total / limit);
  const start = (page - 1) * limit;
  const data = reviews.slice(start, start + limit);
  const avgRating = total > 0 ? reviews.reduce((s, r) => s + r.rating, 0) / total : 0;
  const dist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } as PaginatedReviews['ratingDistribution'];
  reviews.forEach((r) => { (dist as any)[r.rating] = ((dist as any)[r.rating] ?? 0) + 1; });
  return { data, total, page, limit, totalPages, averageRating: parseFloat(avgRating.toFixed(2)), ratingDistribution: dist };
}

// ---------------------------------------------------------------------------
// Reviews Service
// ---------------------------------------------------------------------------
class ReviewsService {
  async getReviews(query?: ReviewsQuery): Promise<ApiResponse<PaginatedReviews>> {
    try {
      const params = new URLSearchParams();
      if (query?.page) params.append('page', query.page.toString());
      if (query?.limit) params.append('limit', query.limit.toString());
      if (query?.productId) params.append('productId', query.productId);
      if (query?.customerId) params.append('customerId', query.customerId);
      if (query?.rating) params.append('rating', query.rating.toString());
      if (query?.minRating) params.append('minRating', query.minRating.toString());
      if (query?.maxRating) params.append('maxRating', query.maxRating.toString());
      if (query?.sortBy) params.append('sortBy', query.sortBy);
      if (query?.sortOrder) params.append('sortOrder', query.sortOrder);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const response = await apiService.get<PaginatedReviews>(`/reviews${qs}`);
      return response;
    } catch (error: any) {
      // Fall back to mock data when endpoint is missing (404) or unreachable
      console.warn('Reviews API unavailable, using mock data:', error?.message ?? error);
      return { success: true, data: buildPaginatedResponse(MOCK_REVIEWS, query) };
    }
  }

  async getReviewById(id: string): Promise<ApiResponse<Review>> {
    try {
      return await apiService.get<Review>(`/reviews/${id}`);
    } catch (error: any) {
      const mock = MOCK_REVIEWS.find((r) => r.id === id);
      if (mock) return { success: true, data: mock };
      throw error;
    }
  }

  async getProductReviews(productId: string, page = 1, limit = 10): Promise<ApiResponse<PaginatedReviews>> {
    try {
      return await apiService.get<PaginatedReviews>(`/products/${productId}/reviews?page=${page}&limit=${limit}`);
    } catch (error: any) {
      const filtered = MOCK_REVIEWS.filter((r) => r.productId === productId);
      return { success: true, data: buildPaginatedResponse(filtered, { page, limit }) };
    }
  }

  async createReview(data: CreateReviewDto): Promise<ApiResponse<Review>> {
    try {
      return await apiService.post<Review>('/reviews', data);
    } catch (error: any) {
      const newReview: Review = {
        id: `rev-${Date.now()}`,
        productId: data.productId,
        customerId: data.customerId,
        customerName: 'New Customer',
        rating: data.rating,
        comment: data.comment,
        orderId: data.orderId,
        isVerified: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      MOCK_REVIEWS.unshift(newReview);
      return { success: true, data: newReview };
    }
  }

  async updateReview(id: string, data: UpdateReviewDto): Promise<ApiResponse<Review>> {
    try {
      return await apiService.patch<Review>(`/reviews/${id}`, data);
    } catch (error: any) {
      const idx = MOCK_REVIEWS.findIndex((r) => r.id === id);
      if (idx !== -1) {
        MOCK_REVIEWS[idx] = { ...MOCK_REVIEWS[idx], ...data, updatedAt: new Date().toISOString() };
        return { success: true, data: MOCK_REVIEWS[idx] };
      }
      throw error;
    }
  }

  async deleteReview(id: string): Promise<ApiResponse<void>> {
    try {
      return await apiService.delete<void>(`/reviews/${id}`);
    } catch (error: any) {
      const idx = MOCK_REVIEWS.findIndex((r) => r.id === id);
      if (idx !== -1) MOCK_REVIEWS.splice(idx, 1);
      return { success: true };
    }
  }

  async getReviewStats(): Promise<ApiResponse<ReviewStats>> {
    try {
      return await apiService.get<ReviewStats>('/reviews/stats');
    } catch (error: any) {
      const total = MOCK_REVIEWS.length;
      const averageRating = total > 0 ? MOCK_REVIEWS.reduce((s, r) => s + r.rating, 0) / total : 0;
      const verifiedReviews = MOCK_REVIEWS.filter((r) => r.isVerified).length;
      const ratingDistribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } as ReviewStats['ratingDistribution'];
      MOCK_REVIEWS.forEach((r) => { (ratingDistribution as any)[r.rating]++; });
      return {
        success: true,
        data: {
          totalReviews: total,
          averageRating: parseFloat(averageRating.toFixed(2)),
          verifiedReviews,
          pendingReviews: 0,
          approvedReviews: total,
          rejectedReviews: 0,
          flaggedReviews: 0,
          ratingDistribution,
        },
      };
    }
  }

  async getProductRating(productId: string): Promise<ApiResponse<{
    averageRating: number;
    totalReviews: number;
    ratingDistribution: Record<number, number>;
  }>> {
    try {
      return await apiService.get(`/products/${productId}/rating`);
    } catch (error: any) {
      const filtered = MOCK_REVIEWS.filter((r) => r.productId === productId);
      const totalReviews = filtered.length;
      const averageRating = totalReviews > 0 ? filtered.reduce((s, r) => s + r.rating, 0) / totalReviews : 0;
      const ratingDistribution: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      filtered.forEach((r) => { ratingDistribution[r.rating] = (ratingDistribution[r.rating] ?? 0) + 1; });
      return { success: true, data: { averageRating: parseFloat(averageRating.toFixed(2)), totalReviews, ratingDistribution } };
    }
  }
}

export const reviewsService = new ReviewsService();